import {
  Add,
  ArrowDropDownCircleOutlined,
  ArrowDownward,
  ArrowUpward,
  AttachFile,
  AssignmentTurnedInOutlined,
  CheckBoxOutlined,
  DeleteOutline,
  EmailOutlined,
  EventOutlined,
  ExpandMore,
  HelpOutline,
  LooksOne,
  PeopleOutline,
  PhoneOutlined,
  RadioButtonChecked,
  SaveOutlined,
  Search,
  ShortText,
  StorefrontOutlined,
  Subject,
  SupervisorAccountOutlined,
  ToggleOnOutlined,
} from '@material-ui/icons';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Alert from '../../../../components/common/Alert';
import Button from '../../../../components/common/Button';
import ModernSelect from '../../../../components/common/ModernSelect';
import { ACTION } from '../../../../reducer/projectReducer';
import { TIME_TO_REDIRECT } from '../../../../utils/constants';
import { getAnnouncementLandingPayload } from '../../../../utils/announcementLogo';
import {
  DEFAULT_MENTEE_QUESTION_ID,
  ensureDefaultMenteeQuestion,
  isDefaultMenteeQuestion,
} from '../../../../utils/registrationQuestions';
import ProjectApi from '../../project-api';
import { ProjectContext } from '../../projectContext';

const QUESTION_TYPES = [
  {
    value: 'short_text',
    label: 'Texto corto',
    description: 'Respuesta breve en una sola línea.',
    Icon: ShortText,
  },
  {
    value: 'long_text',
    label: 'Texto largo',
    description: 'Respuesta abierta de varios párrafos.',
    Icon: Subject,
  },
  {
    value: 'file',
    label: 'Archivo o adjunto',
    description: 'Un archivo PDF, Word, Excel o imagen de hasta 10 MB.',
    Icon: AttachFile,
  },
  {
    value: 'consent',
    label: 'Consentimiento o términos',
    description: 'Casilla para aceptar una declaración o los términos.',
    Icon: AssignmentTurnedInOutlined,
  },
  {
    value: 'number',
    label: 'Número',
    description: 'Acepta únicamente valores numéricos.',
    Icon: LooksOne,
  },
  {
    value: 'email',
    label: 'Correo electrónico',
    description: 'Valida el formato del correo ingresado.',
    Icon: EmailOutlined,
  },
  {
    value: 'phone',
    label: 'Teléfono',
    description: 'Solicita un número telefónico de contacto.',
    Icon: PhoneOutlined,
  },
  {
    value: 'date',
    label: 'Fecha',
    description: 'Muestra un selector visual de fecha.',
    Icon: EventOutlined,
  },
  {
    value: 'boolean',
    label: 'Sí / No',
    description: 'Dos alternativas rápidas y excluyentes.',
    Icon: ToggleOnOutlined,
  },
  {
    value: 'single_choice',
    label: 'Selección única',
    description: 'Permite elegir solamente una alternativa.',
    Icon: RadioButtonChecked,
  },
  {
    value: 'select',
    label: 'Lista desplegable',
    description: 'Permite seleccionar una alternativa de una lista.',
    Icon: ArrowDropDownCircleOutlined,
  },
  {
    value: 'autocomplete',
    label: 'Autocompletado',
    description: 'Permite buscar y seleccionar una alternativa predefinida.',
    Icon: Search,
  },
  {
    value: 'multiple_choice',
    label: 'Selección múltiple',
    description: 'Permite elegir más de una alternativa.',
    Icon: CheckBoxOutlined,
  },
];

const AUDIENCE_OPTIONS = [
  {
    value: 'all',
    label: 'Todos los participantes',
    description: 'Visible para emprendedores y mentores.',
    Icon: PeopleOutline,
  },
  {
    value: 'mentee',
    label: 'Solo emprendedores',
    description: 'Se muestra únicamente a quienes postulan como pyme.',
    Icon: StorefrontOutlined,
  },
  {
    value: 'mentor',
    label: 'Solo mentores',
    description: 'Se muestra únicamente a quienes postulan como mentor.',
    Icon: SupervisorAccountOutlined,
  },
];

const MENTOR_AVAILABILITY_OPTIONS = [
  {
    value: 'disabled',
    label: 'Desactivado',
    description: 'No solicita horarios al completar el registro.',
    Icon: ToggleOnOutlined,
  },
  {
    value: 'enabled',
    label: 'Activado',
    description: 'El mentor deberá seleccionar al menos un horario disponible.',
    Icon: EventOutlined,
  },
];

const choiceTypes = new Set([
  'single_choice',
  'multiple_choice',
  'select',
  'autocomplete',
]);

const createId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createOption = (label = '') => ({
  id: createId('option'),
  label,
});

const createQuestion = () => ({
  id: createId('question'),
  type: 'short_text',
  label: '',
  helpText: '',
  placeholder: '',
  consentText: '',
  required: false,
  audience: 'all',
  options: [],
});

const normalizeQuestions = (questions) =>
  Array.isArray(questions)
    ? questions.map((question) => ({
        id: question.id || createId('question'),
        type: question.type || 'short_text',
        label: question.label || '',
        helpText: question.helpText || '',
        placeholder: question.placeholder || '',
        consentText:
          typeof question.consentText === 'string' ? question.consentText : '',
        required: Boolean(question.required),
        audience: question.audience || 'all',
        options: Array.isArray(question.options)
          ? question.options.map((option) => ({
              id: option.id || createId('option'),
              label: option.label || '',
            }))
          : [],
      }))
    : [];

const validateQuestion = (question, index) => {
  if (!question.label.trim()) {
    return `Escribe el enunciado de la pregunta ${index + 1}.`;
  }

  if (question.type === 'consent' && question.consentText.length > 1000) {
    return `El texto de aceptación de la pregunta ${index + 1} puede tener hasta 1000 caracteres.`;
  }

  if (choiceTypes.has(question.type)) {
    const validOptions = question.options.filter((option) =>
      option.label.trim()
    );

    if (validOptions.length < 2) {
      return `La pregunta ${index + 1} necesita al menos dos alternativas.`;
    }
  }

  return '';
};

const QuestionsForm = ({ onPrev }) => {
  const [state, dispatch] = useContext(ProjectContext);
  const history = useHistory();
  const configuredQuestions = state.configuration?.registration?.questions;
  const enforceDefaultMenteeQuestion =
    !state.id ||
    (Array.isArray(configuredQuestions) &&
      configuredQuestions.some(isDefaultMenteeQuestion));
  const [questions, setQuestions] = useState(() => {
    const normalized = normalizeQuestions(configuredQuestions || []);
    return enforceDefaultMenteeQuestion
      ? ensureDefaultMenteeQuestion(normalized)
      : normalized;
  });
  const questionsForStorage = enforceDefaultMenteeQuestion
    ? ensureDefaultMenteeQuestion(questions)
    : questions;
  const [mentorAvailabilityEnabled, setMentorAvailabilityEnabled] = useState(
    () => state.configuration?.registration?.mentorAvailability?.enabled === true
  );
  const [expandedQuestionIds, setExpandedQuestionIds] = useState({});
  const [questionErrors, setQuestionErrors] = useState({});
  const questionHeadingRefs = useRef({});
  const newQuestionId = useRef(null);
  const [messageAlert, setMessageAlert] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (newQuestionId.current) {
      questionHeadingRefs.current[newQuestionId.current]?.focus();
      newQuestionId.current = null;
    }
  }, [questions]);

  const addQuestion = () => {
    const question = createQuestion();
    newQuestionId.current = question.id;
    setQuestions((current) => [...current, question]);
    setExpandedQuestionIds((current) => ({ ...current, [question.id]: true }));
  };

  const toggleQuestion = (questionId) => {
    setExpandedQuestionIds((current) => ({
      ...current,
      [questionId]: !current[questionId],
    }));
  };

  const updateQuestion = (questionId, changes) => {
    setQuestions((current) =>
      current.map((question) => {
        if (question.id !== questionId) return question;
        const updated = { ...question, ...changes };
        return isDefaultMenteeQuestion(question)
          ? ensureDefaultMenteeQuestion([updated])[0]
          : updated;
      })
    );
  };

  const handleTypeChange = (questionId, type) => {
    setQuestions((current) =>
      current.map((question) => {
        if (question.id !== questionId || isDefaultMenteeQuestion(question)) {
          return question;
        }

        return {
          ...question,
          type,
          placeholder: ['file', 'consent'].includes(type) ? '' : question.placeholder,
          options:
            choiceTypes.has(type) && question.options.length < 2
              ? [createOption(), createOption()]
              : question.options,
        };
      })
    );
  };

  const updateOption = (questionId, optionId, label) => {
    setQuestions((current) =>
      current.map((question) =>
        question.id === questionId
          ? {
              ...question,
              options: question.options.map((option) =>
                option.id === optionId ? { ...option, label } : option
              ),
            }
          : question
      )
    );
  };

  const removeOption = (questionId, optionId) => {
    setQuestions((current) =>
      current.map((question) =>
        question.id === questionId
          ? {
              ...question,
              options: question.options.filter(
                (option) => option.id !== optionId
              ),
            }
          : question
      )
    );
  };

  const moveQuestion = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= questions.length) return;

    setQuestions((current) => {
      const next = [...current];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const removeQuestion = (questionId) => {
    if (isLoading || questionId === DEFAULT_MENTEE_QUESTION_ID) return;
    setQuestions((current) => current.filter((question) => question.id !== questionId));
  };

  const buildConfiguration = (normalize = false) => ({
    ...(state.configuration || {}),
    registration: {
      ...(state.configuration?.registration || {}),
      mentorAvailability: {
        ...(state.configuration?.registration?.mentorAvailability || {}),
        enabled: mentorAvailabilityEnabled,
      },
      questions: normalize
        ? questionsForStorage.map(({ consentText, ...question }) => ({
            ...question,
            ...(question.type === 'consent' && consentText?.trim()
              ? { consentText: consentText.trim() }
              : {}),
            label: question.label.trim(),
            helpText: question.helpText.trim(),
            placeholder:
              ['file', 'consent'].includes(question.type)
                ? ''
                : question.placeholder.trim(),
            options: choiceTypes.has(question.type)
              ? question.options
                  .filter((option) => option.label.trim())
                  .map((option) => ({
                    ...option,
                    label: option.label.trim(),
                  }))
              : [],
          }))
        : questionsForStorage,
    },
  });

  const persistConfiguration = (normalize = false) => {
    const configuration = buildConfiguration(normalize);
    dispatch({
      type: ACTION.UPDATE_CONFIGURATION,
      payload: { configuration },
    });
    return configuration;
  };

  const handlePrev = () => {
    if (isLoading) return;
    persistConfiguration();
    onPrev();
  };

  const saveQuestion = (questionId) => {
    if (isLoading) return;
    const index = questions.findIndex((question) => question.id === questionId);
    const validationMessage = validateQuestion(questions[index], index);
    setQuestionErrors((current) => ({
      ...current,
      [questionId]: validationMessage,
    }));
    if (validationMessage) return;

    // Keep the wizard draft in memory. Only handleSubmit writes to the API.
    persistConfiguration();
    setMessageAlert(null);
    setExpandedQuestionIds((current) => ({ ...current, [questionId]: false }));
    questionHeadingRefs.current[questionId]?.focus();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;

    const invalidIndex = questions.findIndex((question, index) =>
      validateQuestion(question, index)
    );
    if (invalidIndex !== -1) {
      const question = questions[invalidIndex];
      setQuestionErrors((current) => ({
        ...current,
        [question.id]: validateQuestion(question, invalidIndex),
      }));
      setExpandedQuestionIds((current) => ({
        ...current,
        [question.id]: true,
      }));
      questionHeadingRefs.current[question.id]?.focus();
      return;
    }

    const configuration = persistConfiguration(true);
    const payload = {
      configuration,
      landing: getAnnouncementLandingPayload(state.landing, state.client),
      login: state.login,
      platform: state.platform,
      status: state.status,
    };

    try {
      setIsLoading(true);
      if (state.id) {
        await ProjectApi.updateProjectAnnouncement(state.id, payload);
      } else {
        await ProjectApi.createClientPlatform(state.client.id, payload);
      }

      setMessageAlert({
        type: 'success',
        message: state.id
          ? 'Convocatoria actualizada exitosamente.'
          : 'Convocatoria creada exitosamente.',
      });
      setTimeout(() => {
        history.push('/clientes/detalles', { client: state.client });
      }, TIME_TO_REDIRECT);
    } catch (error) {
      setMessageAlert({
        type: 'error',
        message:
          error?.data?.message ||
          'No se pudo guardar la configuración de la convocatoria.',
      });
      setIsLoading(false);
    }
  };

  return (
    <form
      className="announcement-form announcement-form--questions"
      onSubmit={handleSubmit}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && event.target.tagName === 'INPUT') {
          event.preventDefault();
        }
      }}
    >
      {messageAlert && (
        <Alert type={messageAlert.type}>{messageAlert.message}</Alert>
      )}

      <div className="wizard__preview-container announcement-form__toolbar">
        <div className="announcement-form__toolbar-copy">
          <span className="announcement-wizard__eyebrow">Etapa 3 de 3</span>
          <h2>Preguntas Convocatoria</h2>
          <p>
            Crea las preguntas adicionales que responderán los participantes
            durante el registro en esta convocatoria.
          </p>
        </div>
        <button
          type="button"
          className="announcement-question__add-button"
          onClick={addQuestion}
          disabled={isLoading}
        >
          <Add />
          Agregar pregunta
        </button>
      </div>

      <div className="wizard__form">
        <section className="announcement-form__card">
          <div className="announcement-form__section-header">
            <span className="announcement-form__section-icon">
              <EventOutlined />
            </span>
            <div>
              <span className="announcement-wizard__eyebrow">
                Configuración exclusiva de mentores
              </span>
              <h3>Disponibilidad horaria de mentores</h3>
              <p>
                Define si los mentores deben indicar sus horarios disponibles
                durante el registro en esta convocatoria. No se muestra a los
                emprendedores.
              </p>
            </div>
          </div>
          <div className="announcement-question__grid announcement-question__grid--two">
            <div className="announcement-question__select-field">
              <span>Solicitar disponibilidad en el registro</span>
              <ModernSelect
                ariaLabel="Disponibilidad horaria de mentores"
                disabled={isLoading}
                options={MENTOR_AVAILABILITY_OPTIONS}
                value={mentorAvailabilityEnabled ? 'enabled' : 'disabled'}
                onChange={(event) =>
                  setMentorAvailabilityEnabled(event.target.value === 'enabled')
                }
              />
            </div>
            <p className="announcement-question__fixed-note">
              Al activarlo, el mentor deberá seleccionar al menos un horario
              para finalizar su registro. Esta configuración fija se guardará
              al pulsar Finalizar; no agrega una pregunta de texto.
            </p>
          </div>
        </section>

        <section className="announcement-form__card">
          <div className="announcement-form__section-header">
            <span className="announcement-form__section-icon">
              <HelpOutline />
            </span>
            <div>
              <span className="announcement-wizard__eyebrow">
                Formulario adicional
              </span>
              <h3>Preguntas del registro</h3>
              <p>
                Guarda cada pregunta para plegarla y seguir agregando otras.
                Puedes abrirla nuevamente para editarla. Todas se guardarán en
                la convocatoria al pulsar Finalizar.
              </p>
            </div>
          </div>

          {questions.length === 0 ? (
            <div className="announcement-question__empty">
              <HelpOutline />
              <strong>No hay preguntas adicionales</strong>
              <span>
                Puedes finalizar sin preguntas o agregar la primera para
                personalizar el registro.
              </span>
            </div>
          ) : (
            <div className="announcement-question__list">
              {questions.map((question, index) => (
                <article
                  className={`announcement-question${
                    expandedQuestionIds[question.id]
                      ? ''
                      : ' announcement-question--collapsed'
                  }`}
                  key={question.id}
                >
                  <header className="announcement-question__header">
                    <h4 className="announcement-question__heading">
                      <button
                        type="button"
                        className="announcement-question__toggle"
                        id={`question-heading-${question.id}`}
                        aria-expanded={Boolean(
                          expandedQuestionIds[question.id]
                        )}
                        aria-controls={`question-details-${question.id}`}
                        ref={(element) => {
                          questionHeadingRefs.current[question.id] = element;
                        }}
                        onClick={() => toggleQuestion(question.id)}
                      >
                        <span
                          className="announcement-question__number"
                          aria-hidden="true"
                        >
                          {index + 1}
                        </span>
                        <strong className="announcement-question__title">
                          {question.label.trim() || `Pregunta ${index + 1}`}
                        </strong>
                        <ExpandMore className="announcement-question__expand-icon" />
                      </button>
                    </h4>
                    <div className="announcement-question__actions">
                      <button
                        type="button"
                        aria-label="Subir pregunta"
                        disabled={isLoading || index === 0}
                        onClick={() => moveQuestion(index, -1)}
                      >
                        <ArrowUpward />
                      </button>
                      <button
                        type="button"
                        aria-label="Bajar pregunta"
                        disabled={isLoading || index === questions.length - 1}
                        onClick={() => moveQuestion(index, 1)}
                      >
                        <ArrowDownward />
                      </button>
                      <button
                        type="button"
                        aria-label="Eliminar pregunta"
                        disabled={isLoading || isDefaultMenteeQuestion(question)}
                        className="announcement-question__delete"
                        onClick={() => removeQuestion(question.id)}
                      >
                        <DeleteOutline />
                      </button>
                    </div>
                  </header>

                  <div
                    className="announcement-question__body"
                    id={`question-details-${question.id}`}
                    role="region"
                    aria-labelledby={`question-heading-${question.id}`}
                    hidden={!expandedQuestionIds[question.id]}
                  >
                    {isDefaultMenteeQuestion(question) && (
                      <p className="announcement-question__fixed-note">
                        Pregunta fija de la convocatoria: texto largo, exclusiva
                        para mentees y de respuesta obligatoria. No se puede
                        eliminar ni cambiar su enunciado, tipo o público.
                      </p>
                    )}
                    {questionErrors[question.id] && (
                      <p className="announcement-question__error" role="alert">
                        {questionErrors[question.id]}
                      </p>
                    )}

                    <div className="announcement-question__grid announcement-question__grid--two">
                      <div className="announcement-question__select-field">
                        <span>Tipo de respuesta</span>
                        <ModernSelect
                          ariaLabel="Tipo de respuesta"
                          disabled={isDefaultMenteeQuestion(question)}
                          options={QUESTION_TYPES}
                          value={question.type}
                          onChange={(event) =>
                            handleTypeChange(question.id, event.target.value)
                          }
                        />
                      </div>
                      <div className="announcement-question__select-field">
                        <span>Dirigida a</span>
                        <ModernSelect
                          ariaLabel="Público de la pregunta"
                          disabled={isDefaultMenteeQuestion(question)}
                          options={AUDIENCE_OPTIONS}
                          value={question.audience}
                          onChange={(event) =>
                            updateQuestion(question.id, {
                              audience: event.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <label className="announcement-question__field">
                      <span>Enunciado de la pregunta</span>
                      <textarea
                        rows="2"
                        maxLength="300"
                        readOnly={isDefaultMenteeQuestion(question)}
                        value={question.label}
                        placeholder="Ej.: ¿Cuál es el principal desafío de tu negocio?"
                        onChange={(event) =>
                          updateQuestion(question.id, {
                            label: event.target.value,
                          })
                        }
                      />
                    </label>

                    <div className="announcement-question__grid announcement-question__grid--two">
                      <label>
                        <span>Texto de ayuda</span>
                        <input
                          type="text"
                          maxLength="300"
                          value={question.helpText}
                          placeholder="Información opcional para orientar la respuesta"
                          onChange={(event) =>
                            updateQuestion(question.id, {
                              helpText: event.target.value,
                            })
                          }
                        />
                      </label>
                      {!['file', 'consent'].includes(question.type) && (
                        <label>
                          <span>Placeholder</span>
                          <input
                            type="text"
                            maxLength="150"
                            value={question.placeholder}
                            placeholder="Texto mostrado dentro del campo"
                            onChange={(event) =>
                              updateQuestion(question.id, {
                                placeholder: event.target.value,
                              })
                            }
                          />
                        </label>
                      )}
                    </div>

                    {question.type === 'consent' && (
                      <>
                        <label className="announcement-question__field">
                          <span>Texto de aceptación</span>
                          <textarea
                            rows="3"
                            maxLength="1000"
                            value={question.consentText}
                            placeholder="Ej.: Acepto los términos y condiciones de la convocatoria."
                            onChange={(event) =>
                              updateQuestion(question.id, {
                                consentText: event.target.value,
                              })
                            }
                          />
                        </label>
                        <p className="announcement-question__fixed-note">
                          Si se deja vacío, se mostrará el enunciado junto a la
                          casilla. Si la respuesta es obligatoria, la persona
                          deberá marcarla para continuar.
                        </p>
                      </>
                    )}

                    {question.type === 'file' && (
                      <p className="announcement-question__fixed-note">
                        Los participantes podrán adjuntar un solo archivo PDF,
                        DOC, DOCX, XLS, XLSX, JPG, JPEG o PNG por pregunta, de
                        hasta 10 MB.
                      </p>
                    )}

                    {choiceTypes.has(question.type) && (
                      <div className="announcement-question__options">
                        <span>Alternativas</span>
                        {question.options.map((option, optionIndex) => (
                          <div
                            className="announcement-question__option"
                            key={option.id}
                          >
                            <span>{optionIndex + 1}</span>
                            <input
                              type="text"
                              maxLength="150"
                              value={option.label}
                              placeholder={`Alternativa ${optionIndex + 1}`}
                              onChange={(event) =>
                                updateOption(
                                  question.id,
                                  option.id,
                                  event.target.value
                                )
                              }
                            />
                            <button
                              type="button"
                              aria-label="Eliminar alternativa"
                              disabled={question.options.length <= 2}
                              onClick={() =>
                                removeOption(question.id, option.id)
                              }
                            >
                              <DeleteOutline />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="announcement-question__option-add"
                          onClick={() =>
                            updateQuestion(question.id, {
                              options: [...question.options, createOption()],
                            })
                          }
                        >
                          <Add /> Agregar alternativa
                        </button>
                      </div>
                    )}

                    <label className="announcement-question__required">
                      <input
                        type="checkbox"
                        disabled={isDefaultMenteeQuestion(question)}
                        checked={question.required}
                        onChange={(event) =>
                          updateQuestion(question.id, {
                            required: event.target.checked,
                          })
                        }
                      />
                      <span>Respuesta obligatoria</span>
                    </label>
                    <div className="announcement-question__footer">
                      <span>
                        El guardado definitivo se realizará al finalizar la
                        convocatoria.
                      </span>
                      <button
                        type="button"
                        className="announcement-question__save-button"
                        disabled={isLoading}
                        onClick={() => saveQuestion(question.id)}
                      >
                        <SaveOutlined />
                        Guardar pregunta
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
          {questions.length > 0 && (
            <button
              type="button"
              className="announcement-question__add-button announcement-question__add-button--bottom"
              onClick={addQuestion}
              disabled={isLoading}
            >
              <Add />
              Agregar otra pregunta
            </button>
          )}
        </section>

        <div className="wizard__action">
          <Button secondary type="button" onClick={handlePrev}>
            Volver
          </Button>
          <Button loading={isLoading} type="submit" disabled={isLoading}>
            Finalizar
          </Button>
        </div>
      </div>
    </form>
  );
};

export default QuestionsForm;
